import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Spinner, Table } from "reactstrap";
import { useHistory } from "react-router-dom";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import TablePagination from "components/TablePagination";
import { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import { listarCuradoria, aprovarCuradoria, recusarCuradoria, iniciarPedidoCuradoria, obterPedidoRede } from "services/onboardingTransportadoraAdminApi";
import { formatDocument } from "utils/formatDoc";
import { formatDateTime } from "utils/formatDate";
import ResultadoPassos from "../AcessoSftpTransportadora/components/ResultadoPassos";

// Fila da Everlog (PO1-2299, R8): entradas TRAVADAS porque a 0001 não é a
// matriz no de-para do embarcador. A Everlog ajusta o cadastro por fora (0001,
// de-para, vínculo, tabelas, pastas) e clica Liberar, que reconfere no servidor.
// Item SEM e-mail veio pela porta única (catálogo, planilha, tabela de frete...):
// liberar não manda convite, só destrava o onboarding da transportadora.
//
// PO1-3283: aba "Pedidos" — o embarcador pediu uma transportadora pela carteira
// (fase 1) e a Everlog inicia o onboarding daqui, ou abre a tela "Onboarding de
// Transportadora" já preenchida com o pedido.
//
// PO1-3647 (épico PO1-3359): aba "Rede Everlog" — pedidos de entrada feitos pela
// página pública (Origem PEDIDO_REDE). Liberar/Recusar são os mesmos botões; o
// "Ver pedido" abre dados, cobertura declarada, anexos e API de cotação.
const STATUS_OPTIONS = [
  { value: "pendente", label: "Travados" },
  { value: "pedidos", label: "Pedidos" },
  { value: "rede", label: "Rede Everlog" },
  { value: "recusado", label: "Recusados" },
  { value: "todos", label: "Todos" },
];

const ehPedidoRede = (row) => Boolean(row && (row.pedidoRede || row.origem === "PEDIDO_REDE"));
const API_TIPO_LABEL = { REST: "REST (JSON)", SOAP: "SOAP (XML)", EDI: "EDI (arquivo)", PORTAL: "Portal web" };
const ladoTexto = (l) => (l ? (l.cidade ? `${l.cidade}/${l.uf || ""}` : l.uf || "—") : "—");
const fmtTamanho = (b) => {
  const n = Number(b) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const PAGE_SIZES = [10, 20, 50, 100];

const ROTULO_MOTIVO = {
  RAIZ_NOVA: "Raiz nova: 0001 não passou na Receita",
  SEM_0001: "Sem 0001",
  INATIVA_0001: "0001 inativa",
  FILIAL_0001: "0001 não é a matriz",
  FORA_DA_0001: "Cadastro fora da 0001",
};

const ESTADO_ONBOARDING_LABEL = {
  OPERACIONAL: "Operacional",
  EM_ONBOARDING: "Em onboarding",
  AGUARDANDO_HOMOLOGACAO: "Aguardando homologação",
  EM_CONFIGURACAO: "Aguardando homologação",
  DEVOLVIDO: "Devolvida",
  EM_CURADORIA: "Foi para a curadoria",
  NAO_CADASTRADA: "Não cadastrada",
  PEDIDO_INICIADO: "Onboarding iniciado",
};

const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const itemStyle = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem", padding: "0.5rem 0.7rem", borderRadius: 8, background: AUTOPILOT_COLORS.insetSurface, fontSize: "0.84rem" };

function EstadoBadge({ estado }) {
  if (estado === "EM_CURADORIA") return <Badge tipo="pend">Travado</Badge>;
  if (estado === "RECUSADO_EVERLOG") return <Badge tipo="lock">Recusado</Badge>;
  if (estado === "PEDIDO_EMBARCADOR") return <Badge tipo="auto">Pedido do embarcador</Badge>;
  if (estado === "PEDIDO_INICIADO") return <Badge tipo="done">Onboarding iniciado</Badge>;
  if (estado === "LIBERADO") return <Badge tipo="done">Liberado</Badge>;
  return <Badge tipo="done">{estado}</Badge>;
}

// PO1-3647: origem do item quando veio da página pública da Rede.
function OrigemBadge({ row }) {
  if (!ehPedidoRede(row)) return null;
  return <Badge tipo="auto">Pedido de entrada na Rede</Badge>;
}

function MotivoBadge({ matriz }) {
  if (!matriz) return null;
  if (matriz.ok) return <Badge tipo="done">Pronto para liberar</Badge>;
  return <Badge tipo="pend">{ROTULO_MOTIVO[matriz.status] || matriz.rotulo}</Badge>;
}

// Checklist da checagem viva (o servidor devolve `matriz.itens`).
function ChecklistMatriz({ matriz }) {
  if (!matriz) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      {(matriz.itens || []).map((i) => (
        <div key={i.chave} style={itemStyle}>
          <span>
            <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{i.rotulo}</span>
            {i.detalhe && <div style={mutedStyle}>{i.detalhe}</div>}
          </span>
          <Badge tipo={i.ok ? "done" : "pend"}>{i.ok ? "OK" : "Pendente"}</Badge>
        </div>
      ))}
    </div>
  );
}

// Contatos do pedido: o item traz só o e-mail do TI (`email`); um payload mais
// rico pode vir em `detalhe`/`contatos`. Não quebra quando faltar.
function contatosDoPedido(row) {
  const fonte = (row && (row.contatos || (row.detalhe && row.detalhe.contatos) || (row.payload && row.payload.contatos))) || null;
  if (fonte && typeof fonte === "object") return fonte;
  return row && row.email ? { ti: { nome: "", email: row.email } } : {};
}

// PO1-3647: corpo do modal "Ver pedido": dados, cobertura declarada, anexos
// (download pela URL pré-assinada) e API de cotação declarada.
function PedidoRedeDetalhe({ dados }) {
  const p = dados.pedido || {};
  const cobertura = dados.cobertura || [];
  const anexos = dados.anexos || [];
  const api = dados.api;
  const linha = (rotulo, valor) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.3rem 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`, fontSize: "0.86rem" }}>
      <span style={mutedStyle}>{rotulo}</span>
      <span style={{ color: AUTOPILOT_COLORS.text, textAlign: "right" }}>{valor || "—"}</span>
    </div>
  );
  return (
    <>
      <SectionCard variant="field" eyebrow="Pedido" style={{ marginBottom: "0.75rem" }}>
        {linha("Razão social", p.razaoSocial)}
        {linha("CNPJ", formatDocument(p.cnpj))}
        {linha("Responsável", p.nomeResponsavel)}
        {linha("E-mail", p.email)}
        {linha("Telefone / WhatsApp", p.telefone)}
        {linha("Enviado em", p.criadoEm ? formatDateTime(p.criadoEm) : "—")}
        {linha("IP", p.ip)}
      </SectionCard>
      <SectionCard variant="field" eyebrow={`Cobertura declarada (${cobertura.length})`} style={{ marginBottom: "0.75rem" }}>
        {!cobertura.length ? (
          <div style={mutedStyle}>Sem rotas.</div>
        ) : (
          <Table size="sm" responsive className="hub-thead-accent" style={{ marginBottom: 0 }}>
            <thead className="text-primary"><tr><th>Origem</th><th>Destino</th></tr></thead>
            <tbody>
              {cobertura.map((r, i) => (
                <tr key={r.id || i}><td>{ladoTexto(r.origem)}</td><td>{ladoTexto(r.destino)}</td></tr>
              ))}
            </tbody>
          </Table>
        )}
      </SectionCard>
      <SectionCard variant="field" eyebrow={`Tabelas de referência (${anexos.length})`} style={{ marginBottom: "0.75rem" }}>
        {!anexos.length ? (
          <div style={mutedStyle}>Nenhum anexo enviado.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {anexos.map((a) => (
              <div key={a.id} style={itemStyle}>
                <span>
                  <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{a.nome}</span>
                  <div style={mutedStyle}>{String(a.formato || "").toUpperCase()} · {fmtTamanho(a.tamanho)} · {a.visivelEmbarcadores ? "visível para embarcadores" : "só a Everlog vê"}</div>
                </span>
                {a.url ? (
                  <a className="btn btn-sm btn-outline-info" href={a.url} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: "nowrap" }}>Baixar</a>
                ) : (
                  <Badge tipo="pend">Sem link</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      <SectionCard variant="field" eyebrow="API de cotação">
        {!api || !api.temApi ? (
          <div style={mutedStyle}>A transportadora não declarou API de cotação.</div>
        ) : (
          <>
            {linha("Tipo", API_TIPO_LABEL[api.tipo] || api.tipo)}
            {linha("Documentação", api.docUrl ? <a href={api.docUrl} target="_blank" rel="noopener noreferrer">{api.docUrl}</a> : "—")}
            {linha("Contato técnico", api.contatoNome)}
            {linha("E-mail do contato", api.contatoEmail)}
            {linha("Observação", api.observacao)}
          </>
        )}
      </SectionCard>
    </>
  );
}

export default function CuradoriaTransportadoras() {
  const { addToast } = useToast();
  const history = useHistory();
  const [status, setStatus] = useState("pendente");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [liberar, setLiberar] = useState(null); // convite
  const [nome, setNome] = useState("");
  const [recusar, setRecusar] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [agindo, setAgindo] = useState(false);
  // PO1-3283: confirmação do "Iniciar onboarding" e o resultado dele.
  const [iniciar, setIniciar] = useState(null);
  const [resultadoInicio, setResultadoInicio] = useState(null);
  // PO1-3647: modal "Ver pedido" da Rede: { row, loading, dados, erro }.
  const [pedidoRede, setPedidoRede] = useState(null);

  const abrirPedidoRede = async (row) => {
    setPedidoRede({ row, loading: true, dados: null, erro: null });
    try {
      const dados = await obterPedidoRede(row.id);
      setPedidoRede({ row, loading: false, dados, erro: null });
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      setPedidoRede({ row, loading: false, dados: null, erro: d.error || err.message });
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await listarCuradoria({ status, q: q || undefined, page, pageSize });
      setData({ items: r.items || r.itens || [], count: Number(r.count !== undefined ? r.count : r.total) || 0 });
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || err.message;
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Erro ao listar a curadoria", description: msg });
    } finally {
      setLoading(false);
    }
  }, [status, q, page, pageSize, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirLiberar = (convite) => {
    setLiberar(convite);
    setNome(convite.nomeSugerido || "");
  };

  const raizNova = liberar && liberar.matriz && liberar.matriz.status === "RAIZ_NOVA";
  // PO1-3647: pedido da Rede pode vir sem a checagem da matriz na lista; o
  // servidor reconfere ao liberar e, se faltar algo, devolve matriz_pendente
  // com o estado (tratado abaixo, sem fechar o modal).
  const redeSemMatriz = Boolean(liberar && ehPedidoRede(liberar) && !liberar.matriz);
  const podeLiberar = liberar && ((liberar.matriz && (liberar.matriz.ok || raizNova)) || redeSemMatriz);
  const liberarSemEmail = !!(liberar && !liberar.email);

  const confirmarLiberar = async () => {
    if (!liberar) return;
    if (raizNova && !nome.trim()) {
      addToast({ place: "tr", type: "warning", title: "Nome obrigatório", description: "Informe o apelido curto para cadastrar a 0001." });
      return;
    }
    setAgindo(true);
    try {
      const out = await aprovarCuradoria(liberar.id, raizNova ? { nome: nome.trim() } : {});
      const convite = out && out.convite;
      const falhou = convite && convite.estado === "FALHA_ENVIO";
      addToast({
        place: "tr",
        type: falhou ? "warning" : "success",
        title: liberarSemEmail ? "Entrada liberada" : "Convite liberado",
        description: liberarSemEmail
          ? "O onboarding da transportadora segue: contatos, homologação, pastas e acesso."
          : falhou
          ? "O e-mail do convite falhou; o embarcador pode reenviar."
          : `Convite enviado para ${liberar.email}.`,
      });
      setLiberar(null);
      carregar();
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      if (d.code === "matriz_pendente") {
        // A regra ainda não passa: mostra o estado atual sem fechar o modal.
        setLiberar((atual) => (atual ? { ...atual, matriz: d.matriz || atual.matriz } : atual));
        addToast({ place: "tr", type: "warning", title: "Ainda não dá para liberar", description: d.error });
      } else {
        addToast({ place: "tr", type: "danger", title: "Não foi possível liberar", description: d.error || err.message });
      }
    } finally {
      setAgindo(false);
    }
  };

  const confirmarRecusar = async () => {
    if (!recusar || !motivo.trim()) return;
    setAgindo(true);
    try {
      await recusarCuradoria(recusar.id, { motivo: motivo.trim() });
      addToast({
        place: "tr",
        type: "success",
        title: "Recusado",
        description: ehPedidoRede(recusar)
          ? "A transportadora recebe o motivo por e-mail."
          : "O embarcador vê a recusa no card da carteira.",
      });
      setRecusar(null);
      setMotivo("");
      carregar();
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({ place: "tr", type: "danger", title: "Não foi possível recusar", description: d.error || err.message });
    } finally {
      setAgindo(false);
    }
  };

  // PO1-3283: roda o Onboarding de Transportadora com os dados do pedido.
  // Resposta { pedido, onboarding: { estado, passos, curadoria } } ou, numa API
  // mais nova, { estado, resultado }; 202 quando foi para a curadoria.
  const confirmarIniciar = async () => {
    if (!iniciar) return;
    setAgindo(true);
    try {
      const out = await iniciarPedidoCuradoria(iniciar.id, {});
      const onboarding = (out && (out.onboarding || out.resultado)) || out || {};
      const estado = (onboarding && onboarding.estado) || (out && out.estado) || null;
      const curadoria = estado === "EM_CURADORIA" || !!(onboarding && onboarding.curadoria);
      setResultadoInicio({
        pedido: iniciar,
        estado,
        ok: onboarding.ok !== undefined ? onboarding.ok : !curadoria,
        passos: onboarding.passos || [],
        curadoria: onboarding.curadoria || (out && out.curadoria) || null,
      });
      addToast({
        place: "tr",
        type: curadoria ? "warning" : "success",
        title: curadoria ? "Pedido foi para a curadoria" : "Onboarding iniciado",
        description: curadoria
          ? "A matriz desta transportadora precisa de ajuste. O item aparece em Travados."
          : `${iniciar.nomeSugerido || formatDocument(iniciar.cnpj)}: ${ESTADO_ONBOARDING_LABEL[estado] || estado || "em andamento"}.`,
      });
      setIniciar(null);
      carregar();
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({ place: "tr", type: "danger", title: "Não foi possível iniciar o onboarding", description: d.error || err.message });
    } finally {
      setAgindo(false);
    }
  };

  // Abre "Onboarding de Transportadora" já preenchido com o pedido (CNPJ,
  // apelido, embarcador, contatos). Sem endpoint novo: vai pelo location.state.
  const abrirNoOnboarding = (row) => {
    const emb = row.embarcador || {};
    const qs = new URLSearchParams();
    if (emb.id) qs.set("embarcadorId", emb.id);
    qs.set("idPedido", row.id);
    history.push({
      pathname: "/app/everlog/acesso-sftp-transportadora",
      search: `?${qs.toString()}`,
      state: {
        pedido: {
          id: row.id,
          cnpj: row.cnpj,
          apelido: row.nomeSugerido || (row.transportadora && row.transportadora.fantasia) || "",
          idEmbarcador: emb.id || null,
          embarcador: emb.nome || "",
          email: row.email || "",
          contatos: contatosDoPedido(row),
        },
      },
    });
  };

  const filtrosBotoes = (
    <ButtonGroup size="sm" aria-label="Filtro de status">
      {STATUS_OPTIONS.map((opt) => (
        <Button key={opt.value} color="info" outline={opt.value !== status} onClick={() => { setStatus(opt.value); setPage(1); }} aria-pressed={opt.value === status}>
          {opt.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  const nomeDe = (row) => (row.transportadora && (row.transportadora.fantasia || row.transportadora.razaoSocial)) || row.nomeSugerido || row.apelido || "—";
  const emPedidos = status === "pedidos";
  const emRede = status === "rede";

  return (
    <div className="content">
      <PageHero
        eyebrow="Jornada da transportadora"
        title="Curadoria de Transportadoras"
        description="Entradas travadas porque a 0001 ainda não é a matriz no de-para do embarcador, e pedidos de transportadora feitos pelo embarcador na carteira. Ajuste o cadastro e clique Liberar, ou inicie o onboarding do pedido."
        actions={<PageHeroRefreshButton onClick={() => carregar()} loading={loading} />}
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar"
        searchHint="CNPJ, transportadora, e-mail ou embarcador"
        searchPlaceholder="Digite CNPJ, nome ou e-mail"
        searchValue={qInput}
        onSearchChange={setQInput}
        onSearchSubmit={(valor) => { setQ(String(valor || "").trim()); setPage(1); }}
        showFiltersButton={false}
        showActions={false}
        extra={filtrosBotoes}
      />

      {resultadoInicio ? (
        <ShellCard
          eyebrow="Onboarding iniciado"
          title={`${resultadoInicio.pedido.nomeSugerido || formatDocument(resultadoInicio.pedido.cnpj)} · ${ESTADO_ONBOARDING_LABEL[resultadoInicio.estado] || resultadoInicio.estado || "em andamento"}`}
          description={
            resultadoInicio.estado === "EM_ONBOARDING"
              ? "Pastas, usuário SFTP e portal são montados depois da resposta; acompanhe em Onboarding de Transportadora."
              : resultadoInicio.estado === "AGUARDANDO_HOMOLOGACAO" || resultadoInicio.estado === "EM_CONFIGURACAO"
              ? "Transportadora nova na Rede: aprove na fila de Homologação para o onboarding seguir."
              : undefined
          }
          action={<Button size="sm" color="secondary" outline onClick={() => setResultadoInicio(null)}>Fechar</Button>}
          style={{ marginTop: "1rem" }}
        >
          {resultadoInicio.passos.length ? <ResultadoPassos resultado={resultadoInicio} embutido /> : null}
        </ShellCard>
      ) : null}

      <ShellCard>
        <SectionCard
          title={emPedidos ? "Pedidos do embarcador" : emRede ? "Pedidos de entrada na Rede Everlog" : "Entradas travadas"}
          description={
            emPedidos
              ? "O embarcador adicionou a transportadora pela carteira com o contato de TI. Iniciar onboarding cadastra (se preciso), grava os contatos e segue para homologação ou direto para pastas e acesso."
              : emRede
              ? "A transportadora pediu para entrar pela página pública e declarou a cobertura. Veja o pedido, confira a 0001 na Receita e libere (sai o convite com o termo da Rede) ou recuse (a transportadora recebe o e-mail de recusa)."
              : "Regra: a 0001 cadastrada e ativa, matriz no de-para deste embarcador, e os outros CNPJs da raiz como filiais dela. Raiz sem cadastro é conferida na Receita e cadastrada ao liberar."
          }
          action={<span style={{ ...mutedStyle, whiteSpace: "nowrap" }}>{data.count} resultado(s)</span>}
        >
          {loading ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
              <span>Carregando…</span>
            </div>
          ) : erro ? (
            <div style={stateBoxStyle} role="alert">Não foi possível carregar a lista: {erro}</div>
          ) : data.items.length === 0 ? (
            <div style={stateBoxStyle}>{emPedidos ? "Nenhum pedido aguardando." : emRede ? "Nenhum pedido de entrada na Rede aguardando." : "Nenhum item neste filtro."}</div>
          ) : (
            <>
              <Table size="sm" responsive hover className="hub-thead-accent">
                <thead className="text-primary">
                  <tr>
                    <th>CNPJ</th>
                    <th>Transportadora</th>
                    <th>Embarcador</th>
                    <th>{emPedidos ? "Contato de TI" : "Motivo"}</th>
                    <th>Estado</th>
                    <th>Criado em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => {
                    const pedido = row.estado === "PEDIDO_EMBARCADOR";
                    return (
                      <tr key={row.id}>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDocument(row.cnpj)}</td>
                        <td>
                          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                            {nomeDe(row)} <OrigemBadge row={row} />
                          </div>
                          {row.email ? <div style={mutedStyle}>{row.email}</div> : <div style={mutedStyle}>Sem e-mail: entrada pela porta única, sem convite</div>}
                          {row.motivoRecusa && <div style={mutedStyle}>Motivo: {row.motivoRecusa}</div>}
                        </td>
                        <td>{row.embarcador && row.embarcador.nome}</td>
                        <td>
                          {pedido ? (
                            <span style={mutedStyle}>{row.email || "—"}</span>
                          ) : (
                            <>
                              <MotivoBadge matriz={row.matriz} />
                              {row.matriz && row.matriz.cnpj0001 && <div style={mutedStyle}>0001: {formatDocument(row.matriz.cnpj0001)}</div>}
                            </>
                          )}
                        </td>
                        <td><EstadoBadge estado={row.estado} /></td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.creationTime || row.criadoEm)}</td>
                        <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                          {ehPedidoRede(row) && (
                            <>
                              <Button size="sm" color="info" outline onClick={() => abrirPedidoRede(row)} title="Dados do pedido, cobertura declarada, anexos e API de cotação">Ver pedido</Button>{" "}
                            </>
                          )}
                          {row.estado === "EM_CURADORIA" && (
                            <>
                              <Button size="sm" color="success" outline onClick={() => abrirLiberar(row)}>Liberar</Button>{" "}
                              <Button size="sm" color="danger" outline onClick={() => { setRecusar(row); setMotivo(""); }}>Recusar</Button>
                            </>
                          )}
                          {pedido && (
                            <>
                              <Button size="sm" color="success" outline onClick={() => setIniciar(row)}>Iniciar onboarding</Button>{" "}
                              {/* Sem "Recusar" aqui: POST /curadoria/:id/recusar só aceita EM_CURADORIA (409 para pedido). */}
                              <Button size="sm" color="info" outline title="Abrir a tela Onboarding de Transportadora já preenchida com este pedido" onClick={() => abrirNoOnboarding(row)}>
                                Abrir no onboarding
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <TablePagination page={page} pageSize={pageSize} total={data.count} pageSizes={PAGE_SIZES} itemLabel="item(ns)" onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}
        </SectionCard>
      </ShellCard>

      <Modal isOpen={!!liberar} toggle={agindo ? undefined : () => setLiberar(null)} modalClassName="modal-align-top" fade={false}>
        {liberar && (
          <>
            <ModalBody style={{ padding: "1.5rem" }}>
              <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>
                {liberarSemEmail ? "Liberar entrada" : "Liberar convite"}
              </div>
              <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
                {nomeDe(liberar)} · CNPJ {formatDocument(liberar.cnpj)} · entrada de {liberar.embarcador && liberar.embarcador.nome}
                {liberar.email ? ` · convite para ${liberar.email}` : " · sem convite (porta única)"}
              </div>
              {raizNova ? (
                <>
                  <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.86rem", marginBottom: "0.75rem" }}>
                    A raiz não tem cadastro na Rede. Ao liberar, a 0001 ({formatDocument(liberar.matriz.cnpj0001)}) é conferida na Receita de novo (ATIVA e matriz) e cadastrada
                    {liberarSemEmail ? "; o onboarding da transportadora segue." : "; o convite sai para ela."}
                  </div>
                  <FormGroup>
                    <Label style={{ color: AUTOPILOT_COLORS.text }}>Apelido curto *</Label>
                    <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={100} placeholder='Ex.: "União Transportes"' />
                    <div style={{ ...mutedStyle, marginTop: "0.25rem" }}>
                      Vira o nome da pasta no SFTP. Nome que parece razão social fica pendente de revisão de apelido (PO1-2658); não trava mais o onboarding.
                    </div>
                  </FormGroup>
                </>
              ) : (
                <>
                  <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.86rem", marginBottom: "0.75rem" }}>
                    {redeSemMatriz
                      ? "Pedido de entrada na Rede Everlog: ao liberar, a 0001 é conferida na Receita e cadastrada se preciso, e o convite sai com o termo da Rede."
                      : <>Conferido agora no de-para de {liberar.embarcador && liberar.embarcador.nome}:</>}
                  </div>
                  <ChecklistMatriz matriz={liberar.matriz} />
                  {!podeLiberar && (
                    <div style={{ ...mutedStyle, marginTop: "0.75rem" }}>
                      Ajuste o cadastro (0001, de-para deste embarcador, vínculo, tabelas de frete e pastas SFTP) e clique Liberar para conferir de novo. Não há liberação por exceção.
                    </div>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setLiberar(null)} disabled={agindo}>Fechar</Button>
              <Button color="success" onClick={confirmarLiberar} disabled={agindo || !podeLiberar}>
                {agindo ? <Spinner size="sm" /> : liberarSemEmail ? "Aprovar e iniciar onboarding" : "Liberar e enviar convite"}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={!!iniciar} toggle={agindo ? undefined : () => setIniciar(null)} modalClassName="modal-align-top" fade={false}>
        {iniciar && (
          <>
            <ModalBody style={{ padding: "1.5rem" }}>
              <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Iniciar onboarding</div>
              <div style={{ ...mutedStyle, marginBottom: "0.75rem" }}>
                {nomeDe(iniciar)} · CNPJ {formatDocument(iniciar.cnpj)} · pedido de {iniciar.embarcador && iniciar.embarcador.nome}
                {iniciar.email ? ` · TI: ${iniciar.email}` : ""}
              </div>
              <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.86rem" }}>
                A transportadora entra pela porta única (cadastro na matriz 0001, vínculo e contatos). Se for nova na Rede, vai para a fila de Homologação; se já é homologada, pastas, usuário SFTP (modo senha) e portal são montados em seguida. Para escolher chave SSH ou outro e-mail, use "Abrir no onboarding".
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setIniciar(null)} disabled={agindo}>Cancelar</Button>
              <Button color="success" onClick={confirmarIniciar} disabled={agindo}>{agindo ? <Spinner size="sm" /> : "Iniciar onboarding"}</Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* PO1-3647: pedido de entrada na Rede Everlog (só leitura). */}
      <Modal isOpen={!!pedidoRede} toggle={() => setPedidoRede(null)} modalClassName="modal-align-top" fade={false} size="lg">
        {pedidoRede && (
          <>
            <ModalBody style={{ padding: "1.5rem" }} data-testid="modal-pedido-rede">
              <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Pedido de entrada na Rede Everlog</div>
              <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
                {nomeDe(pedidoRede.row)} · CNPJ {formatDocument(pedidoRede.row.cnpj)} · protocolo #{pedidoRede.row.id}
              </div>
              {pedidoRede.loading ? (
                <div style={stateBoxStyle}><Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /><span>Carregando o pedido…</span></div>
              ) : pedidoRede.erro ? (
                <div style={stateBoxStyle} role="alert">Não foi possível carregar o pedido: {pedidoRede.erro}</div>
              ) : pedidoRede.dados ? (
                <PedidoRedeDetalhe dados={pedidoRede.dados} />
              ) : null}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setPedidoRede(null)}>Fechar</Button>
              {pedidoRede.row.estado === "EM_CURADORIA" && (
                <>
                  <Button color="danger" outline onClick={() => { setRecusar(pedidoRede.row); setMotivo(""); setPedidoRede(null); }}>Recusar</Button>
                  <Button color="success" onClick={() => { abrirLiberar(pedidoRede.row); setPedidoRede(null); }}>Liberar</Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={!!recusar} toggle={agindo ? undefined : () => setRecusar(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>
            {ehPedidoRede(recusar) ? "Recusar pedido de entrada na Rede" : recusar && recusar.estado === "PEDIDO_EMBARCADOR" ? "Recusar pedido" : "Recusar entrada"}
          </div>
          {/* PO1-3647: no pedido da Rede quem recebe o motivo é a transportadora, por e-mail. */}
          <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
            {ehPedidoRede(recusar)
              ? "A transportadora recebe o motivo por e-mail e pode fazer um novo pedido pela mesma página."
              : "O embarcador vê o motivo no card da carteira por 30 dias."}
          </div>
          <FormGroup>
            <Label style={{ color: AUTOPILOT_COLORS.text }}>Motivo *</Label>
            <Input type="textarea" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 500))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRecusar(null)} disabled={agindo}>Cancelar</Button>
          <Button color="danger" onClick={confirmarRecusar} disabled={agindo || !motivo.trim()}>{agindo ? <Spinner size="sm" /> : "Recusar"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
